/**
 * Call-out classification and queue (PMA-BCAST-004).
 * Automatic on the released event; producer overrides: per-type enable and
 * duration (layout), dismiss and replay (control messages). An undone event
 * withdraws its call-out (current or queued).
 */
import type { MatchEvent } from "../../store/useMatchLogStore";
import { classifyRawCardType } from "../logger/utils/publicHeaderEvents";
import type { BroadcastLayout, CalloutKind } from "../../lib/broadcastApi";
import { eventKey } from "./delayBuffer";

export interface Callout {
  /** Event key; unique per call-out. */
  id: string;
  kind: CalloutKind;
  event: MatchEvent;
  /** Ms already elapsed of its window when it was introduced late (snapshot). */
  ageMs?: number;
}

export function classifyCallout(event: MatchEvent): CalloutKind | null {
  switch (event.type) {
    case "Shot": {
      const shotType = String(event.data?.shot_type ?? "").toLowerCase();
      if (shotType === "penalty" || shotType === "pen") return "penalty";
      return event.data?.outcome === "Goal" ? "goal" : null;
    }
    case "Goal":
      return "goal";
    case "Card": {
      const raw = classifyRawCardType(
        String(event.data?.card_type ?? "").toLowerCase(),
      );
      if (raw === "yellow") return "yellow_card";
      if (raw === "red" || raw === "yellowSecond") return "red_card";
      return null;
    }
    case "Foul":
      return "foul";
    default:
      return null;
  }
}

export interface CalloutState {
  current: Callout | null;
  queue: Callout[];
  last: Callout | null;
}

export const INITIAL_CALLOUT_STATE: CalloutState = {
  current: null,
  queue: [],
  last: null,
};

export type CalloutAction =
  | { type: "enqueue"; callout: Callout }
  | { type: "expire"; id: string }
  | { type: "dismiss" }
  | { type: "replay" }
  | { type: "withdraw"; id: string };

const advance = (state: CalloutState): CalloutState => {
  const [next, ...rest] = state.queue;
  return next
    ? { current: next, queue: rest, last: next }
    : { current: null, queue: [], last: state.last };
};

export function calloutReducer(
  state: CalloutState,
  action: CalloutAction,
): CalloutState {
  switch (action.type) {
    case "enqueue": {
      if (
        state.current?.id === action.callout.id ||
        state.queue.some((c) => c.id === action.callout.id)
      )
        return state;
      if (!state.current) {
        return {
          current: action.callout,
          queue: state.queue,
          last: action.callout,
        };
      }
      return { ...state, queue: [...state.queue, action.callout] };
    }
    case "expire":
      return state.current?.id === action.id ? advance(state) : state;
    case "dismiss":
      return state.current ? advance(state) : state;
    case "replay": {
      if (!state.last) return state;
      if (!state.current) {
        return { ...state, current: state.last };
      }
      return state;
    }
    case "withdraw": {
      const queue = state.queue.filter((c) => c.id !== action.id);
      const last = state.last?.id === action.id ? null : state.last;
      if (state.current?.id === action.id) {
        return advance({ ...state, queue, last });
      }
      return { ...state, queue, last };
    }
    default:
      return state;
  }
}

/** Build the call-out for a released event under the current layout, if any. */
export function buildCallout(
  event: MatchEvent,
  layout: BroadcastLayout,
  ageMs = 0,
): Callout | null {
  const kind = classifyCallout(event);
  if (!kind) return null;
  if (!layout.callouts?.[kind]?.enabled) return null;
  return ageMs > 0
    ? { id: eventKey(event), kind, event, ageMs }
    : { id: eventKey(event), kind, event };
}

const CLOCK_RE = /^(\d{1,3}):(\d{2})(?:\.\d{1,3})?$/;

/**
 * Broadcast minute label: the minute in progress, so 00:xx reads 1' and
 * 12:34 reads 13'. Broadcast-only; the shared `formatClockMinute` is left
 * untouched. Returns "" for anything that is not MM:SS[.mmm].
 */
export function broadcastMinute(matchClock: string): string {
  if (typeof matchClock !== "string") return "";
  const m = matchClock.match(CLOCK_RE);
  if (!m) return "";
  return `${Math.floor((parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) / 60) + 1}'`;
}
